/**
 * GENERATED FILE — DO NOT EDIT BY HAND.
 *
 * The ISO-4217 exponent (number of minor-unit digits) for every currency in circulation whose exponent is
 * NOT 2. Two is the default, so listing only the exceptions keeps this file 23 lines instead of 155 — and
 * makes the whole table reviewable in one screen.
 *
 * Regenerate from the backend's JDK, inside the same image the backend build uses, so the front and the
 * `currency` reference table can never disagree:
 *
 *   cd packages/money
 *   docker run --rm -v "$PWD:/w" -w /w gradle:8.11-jdk21 \
 *     java tools/GenExponents.java > src/exponents.generated.ts
 *
 * (That command used to name a `GenTs.java` that was never committed — an independent review caught it, so
 * the table was unreproducible exactly where it claims to be derived. `tools/GenExponents.java` is real.)
 *
 * **The image matters, and BGN is the proof.** `Locale.getISOCountries()` → `Currency.getInstance(locale)`
 * MOVES between JDK patch releases: 21.0.11 knows Bulgaria adopted the euro and drops BGN, 21.0.5 still has
 * it. Regenerating on a laptop therefore DELETES the BGN row — which must not happen, because a row is never
 * removed from this table: an order taken in a since-withdrawn currency still has to render. If the generator
 * output is shorter than the file, diff it before overwriting.
 *
 * WHY THIS FILE EXISTS AT ALL — the browser already knows currency decimals, via
 * `new Intl.NumberFormat(locale, { style: 'currency', currency }).resolvedOptions().maximumFractionDigits`.
 * It is the wrong number. Measured on Node 22 across the 218 ISO codes: **CLDR disagrees with ISO on 28 of
 * them** — AFN, ALL, COP, HUF, IDR, IQD, IRR, KPW, LAK, LBP, MGA, MMK, MRO, PKR, SLL, SOS, STD, SYP, TMM,
 * YER, ZMK, ZWD (jdk=2 or 3, intl=0) and BEF, BYB, GRD, PTE, ROL, TPE (jdk=0, intl=2).
 *
 * The reason is that the two tables answer different questions. CLDR describes how people WRITE an amount
 * (a Hungarian does not write the fillérs of a forint); ISO describes the unit the amount is STORED in. We
 * store integer minor units, so ISO is the only correct divisor. Using CLDR's would report a Hungarian or
 * Pakistani store's revenue ×100.
 *
 * `Intl` is still the right tool for turning the resulting number into a string — grouping, symbol
 * placement, RTL. Divide with this table, format with `Intl`. See `formatMoney` in ./money.ts.
 */
export const MINOR_UNIT_EXPONENTS = {
    BHD: 3,
    BIF: 0,
    CLP: 0,
    DJF: 0,
    GNF: 0,
    IQD: 3,
    ISK: 0,
    JOD: 3,
    JPY: 0,
    KMF: 0,
    KRW: 0,
    KWD: 3,
    LYD: 3,
    OMR: 3,
    PYG: 0,
    RWF: 0,
    TND: 3,
    UGX: 0,
    VND: 0,
    VUV: 0,
    XAF: 0,
    XOF: 0,
    XPF: 0,
};
/** What a currency not listed above uses. 132 of the 155 circulating currencies are two-decimal. */
export const DEFAULT_MINOR_UNIT_EXPONENT = 2;
/**
 * Every currency a store may be CREATED in — the same 155 rows as the backend's `currency` table, derived
 * the same way and extracted from the very same seed file (`platform/0004-currency-iso-full.sql`), so the
 * picker can never offer a code the API will refuse.
 *
 * It used to be derived on the fly from the dashboard's country catalog, which had drifted in BOTH
 * directions: it offered ANG, CKD, CUC and ZWL — CKD is not an ISO-4217 code at all, CUC was abolished in
 * 2021 and ZWL replaced by ZWG in 2024 — while missing CUP, SHP, SVC, XCG and ZWG. A seller who picked one
 * of the four got a 422 with nothing on screen to explain it.
 *
 * This list is deliberately NOT the same thing as {@link MINOR_UNIT_EXPONENTS}. That table must keep
 * answering for codes NOT in this list, because a store created years ago in a currency since withdrawn
 * still has orders to render. Sellable is a write-path question; the exponent is a read-path one.
 */
export const SELLABLE_CURRENCIES = [
    'AED',
    'AFN',
    'ALL',
    'AMD',
    'AOA',
    'ARS',
    'AUD',
    'AWG',
    'AZN',
    'BAM',
    'BBD',
    'BDT',
    'BGN',
    'BHD',
    'BIF',
    'BMD',
    'BND',
    'BOB',
    'BRL',
    'BSD',
    'BTN',
    'BWP',
    'BYN',
    'BZD',
    'CAD',
    'CDF',
    'CHF',
    'CLP',
    'CNY',
    'COP',
    'CRC',
    'CUP',
    'CVE',
    'CZK',
    'DJF',
    'DKK',
    'DOP',
    'DZD',
    'EGP',
    'ERN',
    'ETB',
    'EUR',
    'FJD',
    'FKP',
    'GBP',
    'GEL',
    'GHS',
    'GIP',
    'GMD',
    'GNF',
    'GTQ',
    'GYD',
    'HKD',
    'HNL',
    'HTG',
    'HUF',
    'IDR',
    'ILS',
    'INR',
    'IQD',
    'IRR',
    'ISK',
    'JMD',
    'JOD',
    'JPY',
    'KES',
    'KGS',
    'KHR',
    'KMF',
    'KPW',
    'KRW',
    'KWD',
    'KYD',
    'KZT',
    'LAK',
    'LBP',
    'LKR',
    'LRD',
    'LSL',
    'LYD',
    'MAD',
    'MDL',
    'MGA',
    'MKD',
    'MMK',
    'MNT',
    'MOP',
    'MRU',
    'MUR',
    'MVR',
    'MWK',
    'MXN',
    'MYR',
    'MZN',
    'NAD',
    'NGN',
    'NIO',
    'NOK',
    'NPR',
    'NZD',
    'OMR',
    'PAB',
    'PEN',
    'PGK',
    'PHP',
    'PKR',
    'PLN',
    'PYG',
    'QAR',
    'RON',
    'RSD',
    'RUB',
    'RWF',
    'SAR',
    'SBD',
    'SCR',
    'SDG',
    'SEK',
    'SGD',
    'SHP',
    'SLE',
    'SOS',
    'SRD',
    'SSP',
    'STN',
    'SVC',
    'SYP',
    'SZL',
    'THB',
    'TJS',
    'TMT',
    'TND',
    'TOP',
    'TRY',
    'TTD',
    'TWD',
    'TZS',
    'UAH',
    'UGX',
    'USD',
    'UYU',
    'UZS',
    'VES',
    'VND',
    'VUV',
    'WST',
    'XAF',
    'XCD',
    'XCG',
    'XOF',
    'XPF',
    'YER',
    'ZAR',
    'ZMW',
    'ZWG',
];
//# sourceMappingURL=exponents.generated.js.map